/**
 * ManifestDetail — per-contract drill-down at /manifest/:id.
 *
 * Renders the contract header, horizontal tab nav, the active tab pane,
 * and the persistent CoPilot rail. All tabs share the same activeOrder
 * via ManifestContext.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useManifest } from './ManifestContext';
import DetailTabs from './components/DetailTabs';
import CoPilotRail from './components/CoPilotRail';
import OverviewTab from './tabs/OverviewTab';
import ContractTab from './tabs/ContractTab';
import ProductionTab from './tabs/ProductionTab';
import LogisticsTab from './tabs/LogisticsTab';
import InvoiceTab from './tabs/InvoiceTab';
import ActivityTab from './tabs/ActivityTab';

// Post-award only. Pre-award tabs (techspec/sourcing/quotes) moved to FORGE;
// their components remain on disk but are no longer routed here.
const TAB_COMPONENT = {
  overview:   OverviewTab,
  contract:   ContractTab,
  production: ProductionTab,
  logistics:  LogisticsTab,
  invoice:    InvoiceTab,
  activity:   ActivityTab,
};

const ManifestDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { activeOrderId, setActiveOrderId, orders, resetDemo } = useManifest();
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (id && id !== activeOrderId) setActiveOrderId(id);
  }, [id, activeOrderId, setActiveOrderId]);

  const exists = orders.some((o) => o.id === id);
  if (!exists) {
    return (
      <div className="manifest-page">
        <div className="manifest-empty">
          <h2>Contract not found</h2>
          <p>This order doesn't exist or was reset.</p>
          <button className="manifest-btn-primary-sm" onClick={() => navigate('/manifest')}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  const TabPane = TAB_COMPONENT[activeTab] || OverviewTab;

  return (
    <div className="manifest-page manifest-detail">
      <div className="manifest-detail-topbar">
        <Link to="/manifest" className="manifest-detail-back">
          <ArrowLeft size={14} /> Back to dashboard
        </Link>
      </div>

      <DetailTabs active={activeTab} onChange={setActiveTab} />

      <div className="manifest-detail-layout">
        <div className="manifest-detail-main">
          <TabPane onResetDemo={resetDemo} />
        </div>
        <CoPilotRail onTabChange={setActiveTab} />
      </div>
    </div>
  );
};

export default ManifestDetail;
